export const CONSENT_STORAGE_KEY = "almagesto_consent"
export const CONSENT_VERSION = 1
export const CONSENT_TTL_MS = 1000 * 60 * 60 * 24 * 180

const DEFAULT_CONSENT_PREFERENCES = Object.freeze({
  necessary: true,
  analytics: true,
  marketing: false,
})

const isBrowser = () => typeof window !== "undefined"

export const getDefaultConsentPreferences = () => ({
  ...DEFAULT_CONSENT_PREFERENCES,
})

export const normalizeConsentPreferences = preferences => ({
  ...DEFAULT_CONSENT_PREFERENCES,
  marketing: preferences?.marketing === true,
})

const getStorage = () => {
  if (!isBrowser()) {
    return null
  }

  try {
    return window.localStorage || null
  } catch {
    return null
  }
}

export const readStoredConsent = () => {
  const storage = getStorage()

  if (!storage) {
    return null
  }

  try {
    const parsed = JSON.parse(storage.getItem(CONSENT_STORAGE_KEY))

    if (
      parsed?.version !== CONSENT_VERSION ||
      typeof parsed?.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now()
    ) {
      storage.removeItem(CONSENT_STORAGE_KEY)
      return null
    }

    return {
      ...parsed,
      preferences: normalizeConsentPreferences(parsed.preferences),
    }
  } catch {
    storage.removeItem(CONSENT_STORAGE_KEY)
    return null
  }
}

export const hasStoredConsentChoice = () => Boolean(readStoredConsent())

export const getConsentPreferences = () =>
  readStoredConsent()?.preferences || getDefaultConsentPreferences()

export const saveConsentPreferences = preferences => {
  const storage = getStorage()
  const consent = {
    version: CONSENT_VERSION,
    savedAt: new Date().toISOString(),
    expiresAt: Date.now() + CONSENT_TTL_MS,
    preferences: normalizeConsentPreferences(preferences),
  }

  if (!storage) {
    return consent
  }

  storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent))

  try {
    window.dispatchEvent(
      new CustomEvent("almagestoConsentChanged", {
        detail: consent.preferences,
      })
    )
  } catch {
    // Older browsers can still persist the choice; the event is progressive.
  }

  return consent
}

export const toGoogleConsentState = preferences => {
  const value = normalizeConsentPreferences(preferences).marketing
    ? "granted"
    : "denied"

  return {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  }
}

export const ensureDataLayer = () => {
  if (!isBrowser()) {
    return null
  }

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }

  return window.gtag
}

export const setDefaultGoogleConsent = () => {
  const gtag = ensureDataLayer()

  if (!gtag || window.__almagestoDefaultConsentSet) {
    return
  }

  window.__almagestoDefaultConsentSet = true
  gtag("consent", "default", {
    ...toGoogleConsentState(getDefaultConsentPreferences()),
    wait_for_update: 500,
  })
  gtag("set", "ads_data_redaction", true)
  gtag("set", "url_passthrough", false)
}

export const updateGoogleConsent = preferences => {
  const gtag = ensureDataLayer()

  if (!gtag) {
    return
  }

  const normalizedPreferences = normalizeConsentPreferences(preferences)

  gtag("consent", "update", toGoogleConsentState(normalizedPreferences))
  window.dataLayer.push({
    event: "almagesto_consent_update",
    marketing_consent: normalizedPreferences.marketing ? "granted" : "denied",
  })
}
